import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Loader2, RefreshCw, Trash2, PlusCircle, AlertTriangle, CheckCircle2, Clock, Info } from 'lucide-react';
import {
  Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MESES_CORTOS, MESES, DEFAULT_PARAMS } from '../../helpers/controlDiarioCalculations';
import { COLORS, GROUPS } from '../../helpers/controlDiarioLayout';
import { computeResumenAnual, RESUMEN_COLUMNS } from '../../helpers/resumenAnualCalculations';
import { generarConsolidado, eliminarConsolidado } from '../../services/controlProduccionConsolidados.services';
import { ChartBox, EmptyChart, COLORS as CHART } from '../registros/chartParts';

const hex = (h) => (h ? `#${h}` : undefined);
const num = (n, dec) => Number(n).toLocaleString('es-CO', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmt = (v, nf) => {
  if (v === null || v === undefined) return '';
  switch (nf) {
    case 'int': return num(v, 0);
    case 'one': return num(v, 1);
    case 'two': return num(v, 2);
    case 'pct': return `${num(v * 100, 1)}%`;
    case 'diff': return v < 0 ? `(${num(Math.abs(v), 0)})` : num(v, 0);
    case 'money': return `$${num(v, 0)}`;
    default: return String(v);
  }
};
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
const border = `1px solid ${hex(COLORS.border)}`;

// Parámetros actuales de la lavandería, en el mismo formato que se guardan en el consolidado
const parametrosActuales = (laundry) => {
  const cp = { ...DEFAULT_PARAMS, ...(laundry?.indicadores?.controlProduccion || {}) };
  return {
    factorCarga: Number(cp.factorCarga),
    toleranciaVariacion: Number(cp.toleranciaVariacion),
    toleranciaClienteAlistado: Number(cp.toleranciaClienteAlistado),
    tarifaKg: Number(laundry?.indicadores?.costoKilo ?? 0),
  };
};
const mismosParametros = (a, b) => ['factorCarga', 'toleranciaVariacion', 'toleranciaClienteAlistado', 'tarifaKg']
  .every((k) => Number(a?.[k]) === Number(b?.[k]));

// Reporte "Consolidado anual de producción": gestiona los consolidados mensuales guardados del
// "Control diario de producción" y los compara como la hoja RESUMEN ANUAL del Excel.
const ConsolidadoAnualContent = ({ consolidados, laundry, year }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { data, loading, error, reload } = consolidados;
  const [busyMonth, setBusyMonth] = useState(null);
  const [bulk, setBulk] = useState(null); // { done, total }
  const [toDelete, setToDelete] = useState(null);

  const laundryId = laundry?._id || laundry?.id;
  const actuales = useMemo(() => parametrosActuales(laundry), [laundry]);
  const anual = useMemo(() => computeResumenAnual(data), [data]);

  const hoy = new Date();
  const periodoActual = hoy.getFullYear() * 12 + hoy.getMonth() + 1;
  const esFuturo = (m) => year * 12 + m > periodoActual;
  const generadoPor = { id: user?._id || user?.id || undefined, nombre: user?.nombre || user?.name || user?.email || undefined };

  const generar = async (month) => {
    setBusyMonth(month);
    try {
      await generarConsolidado({ laundryIds: [laundryId], year, month, generadoPor });
      toast({ title: 'Consolidado guardado', description: `${MESES[month - 1]} ${year} calculado con los datos actuales.` });
      await reload();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setBusyMonth(null);
    }
  };

  const generarFaltantes = async () => {
    const faltantes = anual.rows.filter((r) => !r.consolidado && !esFuturo(r.month)).map((r) => r.month);
    if (faltantes.length === 0) {
      toast({ title: 'Nada pendiente', description: 'Todos los meses hasta hoy ya tienen consolidado.' });
      return;
    }
    let errores = 0;
    setBulk({ done: 0, total: faltantes.length });
    for (let i = 0; i < faltantes.length; i += 1) {
      try {
        await generarConsolidado({ laundryIds: [laundryId], year, month: faltantes[i], generadoPor });
      } catch (_) {
        errores += 1;
      }
      setBulk({ done: i + 1, total: faltantes.length });
    }
    setBulk(null);
    toast({
      title: errores ? 'Generación con errores' : 'Consolidados generados',
      description: `${faltantes.length - errores} de ${faltantes.length} meses generados.`,
      variant: errores ? 'destructive' : undefined,
    });
    await reload();
  };

  const confirmarEliminar = async () => {
    const c = toDelete;
    setToDelete(null);
    if (!c) return;
    try {
      await eliminarConsolidado(c._id);
      toast({ title: 'Consolidado eliminado', description: `${MESES[c.month - 1]} ${year}` });
      await reload();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  if (error) {
    return <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-xl p-4">{error}</div>;
  }

  const chartData = anual.rows.map((r) => ({
    mes: MESES_CORTOS[r.month - 1],
    kgLotes: r.consolidado ? r.F : null,
    kgFacturables: r.consolidado ? r.Z : null,
    kgAlistados: r.consolidado ? r.G : null,
    horas: r.consolidado ? r.I : null,
    kgFactHora: r.consolidado ? r.P : null,
    kgAlistHora: r.consolidado ? r.R : null,
    prendasHora: r.consolidado ? r.S : null,
    kgDejados: r.consolidado ? r.AE : null,
    kgPotencial: r.consolidado ? r.AF : null,
  }));
  const hayDatos = anual.rows.some((r) => r.consolidado);

  return (
    <div className="space-y-4" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
      {/* ── Gestión de consolidados del año ───────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-base font-bold text-[#001F41] dark:text-white">Consolidados {year} · {laundry?.nombre}</h2>
            <p className="text-[11.5px] text-[#43474F] dark:text-gray-400 mt-0.5">
              Cada consolidado es una foto del mes calculada con los datos y parámetros del momento. Usa <b>Recalcular</b> si
              se corrigieron datos o cambiaron los parámetros.
            </p>
          </div>
          <Button onClick={generarFaltantes} disabled={!!bulk || loading || !laundryId} className="bg-[#0F3460] hover:bg-[#001F41] text-white">
            {bulk ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-2 h-4 w-4" />}
            {bulk ? `Generando ${bulk.done}/${bulk.total}…` : 'Generar faltantes'}
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {anual.rows.map((r) => {
            const c = r.consolidado;
            const futuro = esFuturo(r.month);
            const difiere = c && !mismosParametros(c.parametros, actuales);
            const busy = busyMonth === r.month;
            return (
              <div key={r.month} className={`rounded-lg border p-3 ${futuro ? 'opacity-50' : ''} ${c ? 'border-[#C9CDD3]' : 'border-dashed border-gray-300 dark:border-gray-600'}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm text-[#001F41] dark:text-white">{MESES[r.month - 1]}</span>
                  {c && !c.enCurso && <CheckCircle2 className="h-4 w-4 text-[#0E9F6E]" />}
                  {c?.enCurso && <Clock className="h-4 w-4 text-[#E9A13B]" />}
                </div>
                <div className="text-[11px] text-[#43474F] dark:text-gray-400 mt-1 min-h-[30px]">
                  {futuro && 'Mes futuro'}
                  {!futuro && !c && 'Sin generar'}
                  {c && (
                    <>
                      {c.enCurso ? 'En curso · ' : 'Generado · '}{fmtFecha(c.updatedAt)}
                      {c.generadoPor?.nombre && <div className="truncate" title={c.generadoPor.nombre}>por {c.generadoPor.nombre}</div>}
                    </>
                  )}
                </div>
                {difiere && (
                  <div className="flex items-start gap-1 text-[10.5px] text-[#B87A18] mt-1" title={`Guardado con factor ${c.parametros.factorCarga}%, tolerancias ${c.parametros.toleranciaVariacion}% / ${c.parametros.toleranciaClienteAlistado}%, tarifa $${c.parametros.tarifaKg}`}>
                    <AlertTriangle className="h-3 w-3 flex-none mt-0.5" /> Parámetros distintos a los actuales
                  </div>
                )}
                {!futuro && (
                  <div className="flex gap-1.5 mt-2">
                    <Button size="sm" variant={c ? 'outline' : 'default'} disabled={busy || !!bulk} onClick={() => generar(r.month)} className={`h-7 px-2 text-xs ${c ? '' : 'bg-[#0F3460] hover:bg-[#001F41] text-white'}`}>
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (c ? <RefreshCw className="h-3 w-3 mr-1" /> : <PlusCircle className="h-3 w-3 mr-1" />)}
                      {!busy && (c ? 'Recalcular' : 'Generar')}
                    </Button>
                    {c && (
                      <Button size="sm" variant="ghost" disabled={busy || !!bulk} onClick={() => setToDelete(c)} className="h-7 px-2 text-red-600 hover:text-red-700" title="Eliminar consolidado">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Resumen anual (hoja RESUMEN ANUAL) ───────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm p-5 space-y-3">
        <div>
          <div className="text-[20px] font-bold" style={{ color: hex(COLORS.navy) }}>CONSOLIDADO ANUAL DE PRODUCCIÓN</div>
          <div className="text-[13px] font-bold mt-1" style={{ color: hex(COLORS.pink) }}>Cliente: {laundry?.nombre || '—'}   |   Año: {year}</div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Cargando consolidados…</div>
        ) : (
          <div className="overflow-auto border border-gray-200 dark:border-gray-700">
            <TooltipProvider delayDuration={150}>
            <table className="border-collapse text-[11px]" style={{ color: hex(COLORS.text) }}>
              <thead>
                <tr>
                  <th className="px-2 py-1 sticky left-0 z-10" style={{ border, background: hex(COLORS.navy), color: '#fff', minWidth: 90 }}>Mes</th>
                  {RESUMEN_COLUMNS.map((c) => (
                    <th key={c.col} className="px-1 py-1 font-bold leading-tight" style={{ border, background: hex(GROUPS[c.group].header), color: '#fff', minWidth: 78 }}>
                      <div className="flex items-center justify-center gap-1">
                        <span>{c.header}</span>
                        {c.desc && (
                          <UiTooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="shrink-0 opacity-75 hover:opacity-100 focus:opacity-100 focus:outline-none" aria-label={`Qué significa ${c.header}`}>
                                <Info className="h-3 w-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-[260px] text-left text-xs font-normal leading-snug">
                              <div className="font-semibold mb-0.5">{c.header}</div>
                              {c.desc}
                            </TooltipContent>
                          </UiTooltip>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {anual.rows.map((r) => (
                  <tr key={r.month}>
                    <td className="px-2 py-[3px] font-bold sticky left-0 z-[1]" style={{ border, background: '#fff', color: hex(COLORS.navy) }}>
                      {MESES[r.month - 1]}
                      {!r.consolidado && !esFuturo(r.month) && <span className="ml-1 font-normal text-[10px] text-gray-400">(sin generar)</span>}
                      {r.consolidado?.enCurso && <span className="ml-1 font-normal text-[10px] text-[#B87A18]">(en curso)</span>}
                    </td>
                    {RESUMEN_COLUMNS.map((c) => (
                      <td key={c.col} className="px-1 py-[3px] text-center tabular-nums whitespace-nowrap"
                        style={{ border, background: r.consolidado ? hex(GROUPS[c.group].fill) || '#fff' : '#F7F7F7', color: hex(GROUPS[c.group].font) }}>
                        {r.month === 1 && c.vsAnterior ? '—' : fmt(r[c.col], c.nf)}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-2 py-1 font-bold sticky left-0 z-[1]" style={{ border, background: hex(COLORS.navy), color: '#fff' }}>TOTAL AÑO</td>
                  {RESUMEN_COLUMNS.map((c) => (
                    <td key={c.col} className="px-1 py-1 text-center font-bold tabular-nums whitespace-nowrap" style={{ border, background: hex(COLORS.navy), color: '#fff' }}>
                      {fmt(anual.totals[c.col], c.nf)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            </TooltipProvider>
          </div>
        )}
        <p className="text-[11px] text-[#43474F] dark:text-gray-400">
          Fuente: consolidados guardados de cada mes. Las columnas de dinero usan el "Valor del kilo" de la lavandería
          {actuales.tarifaKg ? ` ($${num(actuales.tarifaKg, 0)}/kg)` : ' (no configurado: quedan vacías)'}.
        </p>
      </div>

      {/* ── Tendencias del año ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartBox title="Kilos por mes" hint="Kg recibidos (lotes), kg facturables y kg alistados.">
          {!hayDatos ? <EmptyChart text="Sin consolidados en el año." /> : (
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${num(v, 1)} kg`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="kgLotes" name="Kg lotes" fill={CHART.lightBlue} radius={[3, 3, 0, 0]} />
                <Bar dataKey="kgFacturables" name="Kg facturables" fill={CHART.navy} radius={[3, 3, 0, 0]} />
                <Bar dataKey="kgAlistados" name="Kg alistados" fill={CHART.green} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartBox>

        <ChartBox title="Productividad por hora autorizada" hint="Kg facturables, kg alistados y prendas por hora.">
          {!hayDatos ? <EmptyChart text="Sin consolidados en el año." /> : (
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="kg" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="prendas" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => num(v, 1)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="kg" type="monotone" dataKey="kgFactHora" name="Kg fact. / hora" stroke={CHART.navy} strokeWidth={2} connectNulls />
                <Line yAxisId="kg" type="monotone" dataKey="kgAlistHora" name="Kg alist. / hora" stroke={CHART.green} strokeWidth={2} connectNulls />
                <Line yAxisId="prendas" type="monotone" dataKey="prendasHora" name="Prendas / hora" stroke={CHART.coral} strokeWidth={2} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartBox>

        <ChartBox title="Horas autorizadas por mes" hint="Barras: horas · Línea: kg facturables por hora.">
          {!hayDatos ? <EmptyChart text="Sin consolidados en el año." /> : (
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="h" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="kgh" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => num(v, 1)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="h" dataKey="horas" name="Horas autorizadas" fill={CHART.lightBlue} radius={[3, 3, 0, 0]} />
                <Line yAxisId="kgh" type="monotone" dataKey="kgFactHora" name="Kg fact. / hora" stroke={CHART.navy} strokeWidth={2} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartBox>

        <ChartBox title="Conciliación de facturación" hint="Kg dejados de cobrar (bajo el alistado) y kg potencial hasta el tope del cliente.">
          {!hayDatos ? <EmptyChart text="Sin consolidados en el año." /> : (
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${num(v, 1)} kg`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="kgDejados" name="Kg dejados de cobrar" fill={CHART.coral} radius={[3, 3, 0, 0]} />
                <Bar dataKey="kgPotencial" name="Kg potencial hasta tope" fill={CHART.amber} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartBox>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el consolidado de {toDelete ? `${MESES[toDelete.month - 1]} ${year}` : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borra la foto guardada del mes. Los datos del sistema no se tocan: puedes volver a generarlo cuando quieras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarEliminar} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConsolidadoAnualContent;
